import React, { useCallback, useMemo } from 'react';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import useOrderWsModalProductsData from '../../../hooks/inbox/data/useOrderWsModalProductsData';
import RightModalLayout from '../../../layouts/Modal/RightModalLayout';
import { SIDEBAR_ACTIONS } from '../../../../constants/actionsConstants';
import { encodeWhatsAppMediaUrl } from '../../../../util/mediaUrlUtils';
import { formatMoney } from '../../../../util/numberUtils';

/**
 * OrderWsModalProductsMC - Componente para mostrar los productos de un pedido WhatsApp en el Inbox.
 */
const OrderWsModalProductsMC = ({ message, onClick }) => {
  const dispatch = useDispatch();
  const { products, total, senderName, createdTime, t, i18n } = useOrderWsModalProductsData(message);
  // Formatear fecha con Intl.DateTimeFormat para mejor internacionalización
  const formattedDateTime = useMemo(() => {
    if (!createdTime) return '';
    const lang = i18n.language || 'es';
    const formatPattern = t('orders.modal.date_format');
    const formatted = moment(createdTime).local().locale(lang).format(formatPattern);
    return formatted.replace(/( de )([a-z])/g, (_, p1, p2) => p1 + p2.toUpperCase());
  }, [createdTime, i18n.language, t]);

  const closeModal = useCallback(() => {
    dispatch({
      type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR,
      payload: { componentKey: 'OrderWsModalProductsMC' }
    });
  }, [dispatch]);

  const handleProductImageClick = useCallback((url) => {
    const safeUrl = encodeWhatsAppMediaUrl(url);
    if (safeUrl && typeof onClick === 'function') onClick(safeUrl);
  }, [onClick]);

  return (
    <div className="tw-wrapper">
      <RightModalLayout
        title={t('orders.inbound')}
        closeAction={closeModal}
      >
        <div className="tw-w-full mx-auto">
          <div className="mb-4">
            <div className="tw-font-semibold tw-text-sm cursor-pointer">{senderName}</div>
          </div>
          <div className="space-y-4">
            {products.map((product) => {
              const productImageUrl = encodeWhatsAppMediaUrl(product.url);
              return (
                <div key={product.key} className="tw-flex tw-items-center tw-justify-between tw-bg-gray-50 tw-rounded-lg tw-p-3">
                  <div className="tw-flex tw-items-center tw-gap-3">
                    {productImageUrl ? (
                      <img
                        src={productImageUrl}
                        className="tw-w-12 tw-h-12 object-cover tw-rounded-lg tw-cursor-pointer"
                        onClick={() => handleProductImageClick(product.url)}
                        alt={product.name || 'product-image'}
                      />
                    ) : (
                      <div className="tw-w-12 tw-h-12 tw-rounded-lg tw-bg-gray-200 tw-flex tw-items-center tw-justify-center tw-text-gray-400">
                        <i className="fas fa-image" aria-hidden="true" />
                      </div>
                    )}
                    <div>
                      <div className="tw-font-bold tw-text-sm">{product.name}</div>
                      <div className="tw-text-gray-500 tw-text-xs">
                        {t('orders.items')}
                        {': '}
                        <span className="tw-ml-1">{product.quantity}</span>
                      </div>
                    </div>
                  </div>
                  <div className="tw-font-bold tw-text-sm tw-text-right">
                    <span>$</span>
                    <span className="tw-ml-1">{formatMoney((product.price * (product.quantity || 1)) / 1000)}</span>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="tw-border-t tw-mt-6 tw-pt-4 tw-flex-col">
            <div className="tw-flex tw-justify-between tw-items-center">
              <span className="tw-font-semibold">{t('orders.modal.total_estimated')}</span>
              <span className="tw-font-bold">
                $
                {formatMoney(total / 1000)}
              </span>
            </div>
            <div className="tw-text-gray-400 text-xs">{formattedDateTime}</div>
          </div>
        </div>
      </RightModalLayout>
    </div>
  );
};

export default OrderWsModalProductsMC;
